import React from 'react';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import DOMPurify from 'isomorphic-dompurify';
import { withTenantSsr } from '../lib/withTenantSsr';
import { BrandProvider } from '../components/branding/BrandProvider';
import { HeaderMinimal } from '../components/public/HeaderMinimal';
import { HeroCompact } from '../components/public/HeroCompact';
import { EventCard } from '../components/public/EventCard';
import { FooterMinimal } from '../components/public/FooterMinimal';
import { TenantBranding } from '../lib/types/branding';
import prisma from '../lib/prisma';

interface Event {
  id: number;
  slug?: string;
  title: string;
  startAt: string;
  flyerUrl?: string;
  minPrice?: number;
}

interface LandingPageProps {
  events: Event[];
  organisation: {
    id: string;
    name: string;
    branding?: TenantBranding;
    currency: string;
    timezone: string;
  };
  canonicalDomain: string;
}

export default function LandingPage({ events, organisation, canonicalDomain }: LandingPageProps) {
  const pageTitle = `${organisation.name} — Events`;
  const pageDescription = organisation.branding?.orgShortDescription || `Discover upcoming events from ${organisation.name}`;
  const ogImage = organisation.branding?.ogImageUrl || organisation.branding?.heroImageUrl;

  return (
    <BrandProvider 
      branding={organisation.branding}
      orgName={organisation.name}
      currency={organisation.currency}
      timezone={organisation.timezone}
    >
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://${canonicalDomain}`} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://${canonicalDomain}`} />
        
        {/* JSON-LD for Organization */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": organisation.name,
              "url": `https://${canonicalDomain}`,
              ...(ogImage && { "logo": ogImage }),
            }),
          }}
        />
      </Head>

      <div className="min-h-screen bg-gray-50">
        <HeaderMinimal />
        
        <main>
          <HeroCompact />
          
          {/* Events Section */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Upcoming Events</h2>
            </div>
            
            {events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="max-w-md mx-auto">
                  <div 
                    className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-primary)', opacity: 0.1 }}
                  >
                    <svg 
                      className="w-8 h-8"
                      style={{ color: 'var(--color-primary)' }}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Upcoming Events</h3>
                  <p className="text-gray-600">
                    No upcoming events yet. Please check back soon.
                  </p>
                </div>
              </div>
            )}
          </section>
        </main>
        
        <FooterMinimal />
      </div>
    </BrandProvider>
  );
}

export const getServerSideProps = withTenantSsr<LandingPageProps>(
  async (context) => {
    // Get tenant from the tenant context set by withTenantSsr
    const { getTenant } = await import('../lib/tenant');
    const tenant = getTenant();
    try {
      const now = new Date();

      // Fetch upcoming events for this organization
      const events = await prisma.event.findMany({
        where: {
          orgId: tenant.orgId,
          isActive: true,
          dates: {
            some: {
              date: {
                gte: now,
              },
            },
          },
        },
        include: {
          dates: {
            where: {
              date: {
                gte: now,
              },
            },
            orderBy: {
              date: 'asc',
            },
            take: 1, // Get the next upcoming date
          },
          ticketTypes: {
            where: {
              isActive: true,
              isPublic: true,
            },
            orderBy: {
              price: 'asc',
            },
            take: 1, // Get the cheapest ticket for min price
          },
        },
        orderBy: {
          id: 'asc', // Simple ordering by ID for now
        },
        take: 12, // Limit to 12 events
      });

      // Transform events to the format expected by the component
      const transformedEvents: Event[] = events
        .filter(event => event.dates.length > 0) // Only events with upcoming dates
        .map(event => ({
          id: event.id,
          slug: event.slug,
          title: event.title,
          startAt: event.dates[0].date!.toISOString(),
          flyerUrl: event.coverImage || undefined,
          minPrice: event.ticketTypes.length > 0 ? event.ticketTypes[0].price : undefined,
        }));

      // Get the primary domain for canonical URL
      const primaryDomain = await prisma.domain.findFirst({
        where: {
          orgId: tenant.orgId,
          isPrimary: true,
          verifiedAt: { not: null },
        },
      });

      const canonicalDomain = primaryDomain?.domain || `${tenant.organisation.subdomain}.dramatickets.co.uk`;

      return {
        props: {
          events: transformedEvents,
          organisation: {
            id: tenant.organisation.id,
            name: tenant.organisation.name,
            branding: tenant.organisation.branding as TenantBranding || null,
            currency: tenant.organisation.currency,
            timezone: tenant.organisation.timezone,
          },
          canonicalDomain,
        },
      };
    } catch (error) {
      console.error('[LANDING PAGE] Error fetching data:', error);
      
      // Return empty state on error
      return {
        props: {
          events: [],
          organisation: {
            id: tenant.organisation.id,
            name: tenant.organisation.name,
            branding: null,
            currency: tenant.organisation.currency,
            timezone: tenant.organisation.timezone,
          },
          canonicalDomain: `${tenant.organisation.subdomain}.dramatickets.co.uk`,
        },
      };
    }
  }
);
